"use client";

import React, { Component, ErrorInfo, ReactNode } from 'react';
import ButtonFFXIV from '@/app/components/ButtonFFXIV';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
            console.error('GameResult Error Boundary caught an error:', error, errorInfo);
            
            // Check if it's a DOM manipulation error common during hot reload
            if (error.name === 'NotFoundError' && error.message.includes('removeChild')) {
                console.log('This appears to be a hot reload DOM error - will auto-recover');
            }
        }
    }

    handleReset = () => {
        // Try to clean up any problematic DOM nodes before resetting
        if (process.env.NODE_ENV === 'development') {
            try {
                // Clean up any orphaned Leaflet containers
                const orphanedContainers = document.querySelectorAll('.leaflet-container');
                orphanedContainers.forEach(container => {
                    // Only remove containers that seem orphaned (no parent or not properly attached)
                    if (!container.closest('[data-map-container]')) {
                        try {
                            if (container.parentNode) {
                                container.parentNode.removeChild(container);
                            }
                        } catch {
                            // Ignore individual cleanup errors
                        }
                    }
                });
            } catch (error) {
                // If cleanup fails, we'll need to force refresh
                console.warn('Cleanup failed, may need force refresh:', error);
            }
        }
        
        this.setState({ hasError: false, error: undefined });
    };

    handleForceRefresh = () => {
        // Force a refresh of the page to reset everything
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    };

    render() {
        if (this.state.hasError) {
            // In development, show a simple retry button for hot reload errors
            if (process.env.NODE_ENV === 'development' && 
                this.state.error?.name === 'NotFoundError' && 
                this.state.error?.message.includes('removeChild')) {
                
                return (
                    <div className="relative w-full h-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <div className="bg-black/80 backdrop-blur-md rounded-lg border border-white/20 p-8 max-w-md text-center">
                            <div className="text-4xl mb-4">⚠️</div>
                            <h2 className="text-white text-xl font-semibold mb-4">
                                Hot Reload Error
                            </h2>
                            <p className="text-gray-300 text-sm mb-6">
                                A development hot reload error occurred. This is normal during development.
                            </p>
                            <div className="space-y-3">
                                <ButtonFFXIV
                                    text="Try Again"
                                    onClick={this.handleReset}
                                    goldenBorder={true}
                                />
                                <ButtonFFXIV
                                    text="Force Refresh"
                                    onClick={this.handleForceRefresh}
                                    goldenBorder={false}
                                />
                            </div>
                        </div>
                    </div>
                );
            }

            // For other errors, show the custom fallback or default error UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="relative w-full h-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-black/80 backdrop-blur-md rounded-lg border border-white/20 p-8 max-w-md text-center">
                        <div className="text-4xl mb-4">❌</div>
                        <h2 className="text-white text-xl font-semibold mb-4">
                            Something went wrong
                        </h2>
                        <p className="text-gray-300 text-sm mb-6">
                            An error occurred while displaying the game results.
                        </p>
                        <ButtonFFXIV
                            text="Try Again"
                            onClick={this.handleReset}
                            goldenBorder={true}
                        />
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;